import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {forkJoin, Observable} from 'rxjs';
import {LoginService} from './login.service';
import {map, take} from 'rxjs/operators';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {LoginBoxComponent} from './login-box/login-box.component';
import {MatDialog} from '@angular/material/dialog';
import {Globals} from '../../globals';
import {RedirectService} from "../../../data/services/redirect.service";
import {isPlatformBrowser} from "@angular/common";
import {ConfigService} from "../../services/config.service";
import {AuthResponseData} from "./auth-response-data.model";

@Injectable({providedIn: 'root'})
export class LoginGuard  {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly loginService = inject(LoginService);
  private readonly redirectService = inject(RedirectService);
  private readonly dialog = inject(MatDialog);
  private readonly configService = inject(ConfigService);
  private readonly platformId = inject<object>(PLATFORM_ID);


  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot):
    Observable<boolean | UrlTree | undefined> | Promise<boolean | UrlTree> | boolean | UrlTree
  {
    return forkJoin({
      useId: this.useIdentification(),
      auth: this.loginService.getAuthData().pipe(take(1)),
      verint: this.isVerintEnabled()
    }).pipe(map(res => this.redirectTo(res.useId, res.auth, res.verint, route, state)));
  }

  private redirectTo(useIdentification: boolean | undefined, user: AuthResponseData | null, verintConfig: boolean | undefined, route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | undefined
  {
    const isAuth = !!user;
    const useLandingPage = Globals.USE_LANDING_PAGE;
    if (isAuth) {
      return true;
    }

    switch (state.url) {
      case '/complaint':
        return this.router.createUrlTree(['/complaint-without-login']);
      case '/contact':
        if ( !verintConfig ) { break; }
        return this.router.createUrlTree(['/contact-without-login']);
      case '/revocation':
        return this.router.createUrlTree(['/revocation-without-login']);
      case '/sub-change':
        if (useIdentification) {
          return this.router.createUrlTree(['/sub-change-without-login']);
        }
        break;
    }

    if (isPlatformBrowser(this.platformId)) {
      const ssoUsage = this.configService.getConfig('sso.usage');
      if (ssoUsage?.value?.toUpperCase()?.indexOf('OAUTHHASSO') === 0) {
        return this.router.createUrlTree(['/login'], {queryParams: {returnUrl: state.url}});
      } else {
        this.dialog.open(LoginBoxComponent, {data: {url: state.url}, panelClass: 'SSOloginBox'});
      }
    } else {
      this.redirectService.redirect('/login?returnUrl=' + state.url.replace('/', ''));
    }

    if (useLandingPage) {
      return;
    } else {
      return this.router.createUrlTree(['/']);
    }
  }

  private useIdentification(): Observable<boolean | undefined> {
    return this.configService.loadConfig('withoutLogin.useIdentificationForm').pipe(map(config => {
      return config?.asBoolean()
    }));
  }

  private isVerintEnabled(): Observable<boolean | undefined> {
    return this.configService.loadConfig('verint.enabled').pipe(map(config => {
      return config?.asBoolean()
    }));
  }
}
